import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, X, FileText } from "lucide-react";

const MAX_FILES = 5;
const MAX_MB = 25;
const ALLOWED_EXTS = ["pdf", "docx", "xlsx", "png", "jpg", "jpeg"];

const humanSize = (b) => {
  if (b == null) return "";
  const kb = b / 1024; if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
};

export default function CreateRFQ() {
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [form, setForm] = useState({
    title: "", category: "", description: "", quantity: 1, uom: "Each",
    delivery_location: "", delivery_deadline: "", target_budget: "", notes: "", document_ids: [],
  });
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { (async () => {
    const { data } = await api.get("/meta/categories");
    setCategories(data.categories);
    setUoms(data.uoms || ["Each"]);
  })(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      if (docs.length >= MAX_FILES) { toast.error(`You can attach up to ${MAX_FILES} files per RFQ.`); break; }
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED_EXTS.includes(ext)) { toast.error(`${f.name}: unsupported type. Allowed: ${ALLOWED_EXTS.join(", ").toUpperCase()}`); continue; }
      if (f.size > MAX_MB * 1024 * 1024) { toast.error(`${f.name}: exceeds ${MAX_MB} MB`); continue; }
      const fd = new FormData(); fd.append("file", f);
      try {
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setDocs((d) => [...d, data]);
        setForm((s) => ({ ...s, document_ids: [...s.document_ids, data.id] }));
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    }
    e.target.value = "";
  };

  const removeDoc = (id) => {
    setDocs((d) => d.filter((x) => x.id !== id));
    setForm((s) => ({ ...s, document_ids: s.document_ids.filter((x) => x !== id) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) { toast.error("Please select a category"); return; }
    if (!form.uom) { toast.error("Please select a unit of measure"); return; }
    setLoading(true);
    try {
      const payload = { ...form, quantity: Number(form.quantity), target_budget: form.target_budget ? Number(form.target_budget) : null };
      const { data } = await api.post("/rfqs", payload);
      toast.success("RFQ submitted for review");
      nav(`/app/rfqs/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <div className="eyebrow">New request</div>
        <h1 className="font-display text-3xl font-extrabold text-[#0A2540] tracking-tight mt-1">Create RFQ</h1>
        <p className="text-slate-500 mt-2 text-sm">Submit your requirement — our team will review, source suppliers and present a comparison.</p>
      </div>

      <Card className="p-8 border-slate-200">
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label>Title *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="e.g. 25× office ergonomic chairs" data-testid="rfq-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="rfq-category-select"><SelectValue placeholder="Choose category" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Target budget (CAD)</Label>
              <Input type="number" step="0.01" value={form.target_budget} onChange={(e) => setForm({ ...form, target_budget: e.target.value })} placeholder="Optional" data-testid="rfq-budget-input" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Quantity *</Label>
              <Input type="number" step="0.001" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} required min={0.001} data-testid="rfq-quantity-input" />
            </div>
            <div>
              <Label>Unit of measure *</Label>
              <Select value={form.uom} onValueChange={(v) => setForm({ ...form, uom: v })}>
                <SelectTrigger data-testid="rfq-uom-select"><SelectValue placeholder="Select UOM" /></SelectTrigger>
                <SelectContent>{uoms.map((u) => <SelectItem key={u} value={u} data-testid={`uom-opt-${u}`}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Delivery deadline *</Label>
              <Input type="date" value={form.delivery_deadline} onChange={(e) => setForm({ ...form, delivery_deadline: e.target.value })} required data-testid="rfq-deadline-input" />
            </div>
          </div>
          <div>
            <Label>Delivery location *</Label>
            <Input value={form.delivery_location} onChange={(e) => setForm({ ...form, delivery_location: e.target.value })} required placeholder="e.g. Toronto, ON" data-testid="rfq-location-input" />
          </div>
          <div>
            <Label>Description & specifications *</Label>
            <Textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required placeholder="Detailed specs, certifications, brand preferences, packaging, etc." data-testid="rfq-description-input" />
          </div>
          <div>
            <Label>Notes for Caprocure</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="rfq-notes-input" />
          </div>

          <div>
            <Label>Attachments (drawings, specs, PDFs) <span className="text-slate-400 text-xs font-normal">— PDF · DOCX · XLSX · PNG · JPG, up to {MAX_MB} MB each · max {MAX_FILES} files</span></Label>
            <div className="mt-1 border-2 border-dashed border-slate-200 rounded-lg p-4">
              <label className={`flex items-center gap-2 text-sm text-slate-600 ${docs.length >= MAX_FILES ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`} data-testid="rfq-upload-label">
                <Upload className="w-4 h-4" /> {docs.length >= MAX_FILES ? `Maximum ${MAX_FILES} files reached` : "Click to upload files"}
                <input type="file" className="hidden" multiple disabled={docs.length >= MAX_FILES} accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg" onChange={upload} data-testid="rfq-file-input" />
              </label>
              {docs.length > 0 && (
                <div className="mt-3 space-y-2">
                  {docs.map((d) => (
                    <div key={d.id} className="flex items-center justify-between text-sm bg-slate-50 px-3 py-2 rounded">
                      <span className="flex items-center gap-2"><FileText className="w-4 h-4 text-slate-500" /> {d.filename} <span className="text-xs text-slate-400">· {humanSize(d.size)}</span></span>
                      <button type="button" onClick={() => removeDoc(d.id)} className="text-slate-500 hover:text-rose-600" data-testid={`remove-doc-${d.id}`}><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="pt-4 flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => nav(-1)} data-testid="rfq-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-[#0A2540] hover:bg-[#061A2E]" data-testid="rfq-submit-btn">
              {loading ? "Submitting…" : "Submit RFQ"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
